import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { ArrowLeft, RefreshCw, ExternalLink } from 'lucide-react';
import {
  useVerifyOtp,
  useSendOtp,
  type VerifyOtpResult
} from '@features/auth/hooks';
import { OTP_CONFIG } from '@lib/api/auth';
import { OneTimeCodeInput } from '@shared/ui/inputs/OneTimeCodeInput';

interface OtpVerificationFormProps {
  email: string;
  isNewUser: boolean;
  /** Disable OTP submission (e.g., when email validation failed) */
  isSubmitDisabled?: boolean;
  onBack: () => void;
  onSuccess: (
    user: VerifyOtpResult['user'],
    tokens: VerifyOtpResult['tokens'],
    isNewUser: boolean
  ) => void;
}

/**
 * OtpVerificationForm - Elegant OTP Verification
 *
 * Design Philosophy:
 * - Compact, modern OTP input with pill-style grouping
 * - Filled inputs turn emerald with white text
 * - Subtle container with dot separator
 * - Auto-focus and auto-advance between digits
 * - Auto-submit when all digits entered
 * - Paste support for full code
 */
export const OtpVerificationForm: React.FC<OtpVerificationFormProps> = ({
  email,
  isNewUser,
  isSubmitDisabled = false,
  onBack,
  onSuccess
}) => {
  const { t } = useTranslation('auth');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState<number>(
    OTP_CONFIG.RESEND_COOLDOWN_SECONDS
  );
  const codeInputRef = useRef<HTMLInputElement | null>(null);

  const { mutate: verifyOtp, isPending: isVerifying } = useVerifyOtp({
    onSuccess: result => {
      onSuccess(result.user, result.tokens, result.isNewUser);
    },
    onError: err => {
      setError(err.message || t('multiMethod.invalidCode'));
      setCode('');
      setTimeout(() => codeInputRef.current?.focus(), 0);
    }
  });

  const { mutate: resendOtp, isPending: isResending } = useSendOtp({
    onSuccess: () => {
      setResendCooldown(OTP_CONFIG.RESEND_COOLDOWN_SECONDS);
      setError(null);
    },
    onError: err => {
      setError(err.message || t('multiMethod.resendFailed'));
    }
  });

  // Countdown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-submit when all digits entered (unless disabled)
  useEffect(() => {
    if (code.length === OTP_CONFIG.CODE_LENGTH && !isSubmitDisabled) {
      verifyOtp({ email, code });
    }
  }, [code, email, verifyOtp, isSubmitDisabled]);

  const handleCodeChange = useCallback((next: string): void => {
    setError(null);
    setCode(next);
  }, []);

  const handleResend = () => {
    if (resendCooldown === 0 && !isResending) {
      resendOtp({ email });
    }
  };

  // Detect email provider for quick actions
  const emailDomain = email.split('@')[1]?.toLowerCase() || '';
  const isGmail =
    emailDomain.includes('gmail') || emailDomain.includes('googlemail');
  const isOutlook =
    emailDomain.includes('outlook') ||
    emailDomain.includes('hotmail') ||
    emailDomain.includes('live') ||
    emailDomain.includes('msn');

  // Open email provider
  const openGmail = () => window.open('https://mail.google.com', '_blank');
  const openOutlook = () => window.open('https://outlook.live.com', '_blank');

  return (
    <div className="w-full">
      {/* Back Button */}
      <motion.button
        type="button"
        onClick={onBack}
        whileHover={{ x: -2 }}
        className="mb-6 flex items-center text-gray-500 transition-colors hover:text-gray-700"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        <span className="text-sm font-medium">{t('multiMethod.back')}</span>
      </motion.button>

      <div className="sr-only">
        Enter the 6-digit code sent to {email}
        {isNewUser ? ' Creating your account.' : ''}
      </div>

      {/* OTP Input - Single input (paste/autofill friendly) */}
      <div className="mb-6 flex flex-col items-center">
        <OneTimeCodeInput
          value={code}
          onChange={handleCodeChange}
          length={OTP_CONFIG.CODE_LENGTH}
          disabled={isVerifying}
          autoFocus={true}
          ariaLabel={`Enter the ${OTP_CONFIG.CODE_LENGTH}-digit code`}
          inputRef={codeInputRef}
        />
        <p className="mt-2 text-xs text-gray-400">{t('multiMethod.enterCode')}</p>
      </div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 rounded-xl border border-red-100 bg-red-50 p-3 text-center"
        >
          <p className="text-sm text-red-600">{error}</p>
        </motion.div>
      )}

      {/* Loading State */}
      {isVerifying && (
        <div className="mb-4 flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="mr-2 h-5 w-5 rounded-full border-2 border-emerald-500 border-t-transparent"
          />
          <span className="text-sm font-medium text-emerald-600">
            {t('multiMethod.verifying')}
          </span>
        </div>
      )}

      {/* Quick Actions - Open Gmail/Outlook */}
      <div className="mb-6 flex items-center justify-center gap-3">
        {(isGmail || !isOutlook) && (
          <motion.button
            type="button"
            onClick={openGmail}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center rounded-2xl border border-gray-200 bg-white/90 px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-xs transition-all hover:border-gray-300 hover:text-gray-900 hover:shadow-sm"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none">
              <path
                d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0l-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z"
                fill="#EA4335"
              />
            </svg>
            {t('multiMethod.openGmail')}
            <ExternalLink className="ml-1.5 h-3 w-3 opacity-50" />
          </motion.button>
        )}

        {(isOutlook || !isGmail) && (
          <motion.button
            type="button"
            onClick={openOutlook}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center rounded-2xl border border-gray-200 bg-white/90 px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-xs transition-all hover:border-gray-300 hover:text-gray-900 hover:shadow-sm"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none">
              <path
                d="M21.5 6v12c0 .83-.67 1.5-1.5 1.5H4c-.83 0-1.5-.67-1.5-1.5V6c0-.83.67-1.5 1.5-1.5h16c.83 0 1.5.67 1.5 1.5z"
                fill="#0078D4"
              />
              <path d="M21.5 6l-9.5 7-9.5-7" stroke="#fff" strokeWidth="1.5" />
            </svg>
            {t('multiMethod.openOutlook')}
            <ExternalLink className="ml-1.5 h-3 w-3 opacity-50" />
          </motion.button>
        )}
      </div>

      {/* Resend Code */}
      <div className="text-center">
        <p className="mb-1 text-sm text-gray-500">{t('multiMethod.cantFindCode')}</p>
        <button
          type="button"
          onClick={handleResend}
          disabled={resendCooldown > 0 || isResending}
          className={`
            inline-flex items-center text-sm font-medium transition-colors
            ${
              resendCooldown > 0 || isResending
                ? 'cursor-not-allowed text-gray-400'
                : 'text-emerald-600 underline-offset-2 hover:text-emerald-700 hover:underline'
            }
          `}
        >
          <RefreshCw
            className={`mr-1.5 h-3.5 w-3.5 ${isResending ? 'animate-spin' : ''}`}
          />
          {resendCooldown > 0
            ? t('multiMethod.resendIn', { seconds: resendCooldown })
            : isResending
              ? t('multiMethod.sending')
              : t('multiMethod.resendCode')}
        </button>
      </div>
    </div>
  );
};
